import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  ArrowDownUp, 
  Send, 
  Copy, 
  CheckCircle, 
  ExternalLink,
  Info,
  Wallet
} from 'lucide-react'
import { useWeb3Wallet } from '../contexts/Web3WalletProvider'
import { useTransaction } from '../contexts/TransactionContext'
import { ethereumService } from '../services/ethereumService'

const PresalePage: React.FC = () => {
  const { isConnected, balance } = useWeb3Wallet()
  const { addTransaction } = useTransaction()
  
  const [bnbAmount, setBnbAmount] = useState('')
  const [voiceAmount, setVoiceAmount] = useState('')
  const [copied, setCopied] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  })
  
  const PRESALE_PRICE = 0.00001 // BNB per VOICE (1 BNB = 100,000 VOICE)
  const PRESALE_WALLET_ADDRESS = '0x3c027958a3D4C4eDb008ee319692F7D387e6C5fB' // VOICE Smart Contract
  const TOTAL_SUPPLY = 250000000 // 250 Million VOICE tokens
  const PRESALE_END_DATE = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) // 7 days from now
  
  // Countdown timer effect
  React.useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date().getTime()
      const distance = PRESALE_END_DATE.getTime() - now
      
      if (distance > 0) {
        setTimeLeft({
          days: Math.floor(distance / (1000 * 60 * 60 * 24)),
          hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((distance % (1000 * 60)) / 1000)
        })
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 })
      }
    }, 1000)
    
    return () => clearInterval(timer)
  }, [])
  
  // Calculate VOICE tokens from BNB input
  const calculateVoiceFromBnb = (bnbValue: string) => {
    if (!bnbValue || isNaN(Number(bnbValue))) return ''
    const voiceTokens = Number(bnbValue) / PRESALE_PRICE
    return voiceTokens.toFixed(2)
  }
  
  // Calculate BNB from VOICE input
  const calculateBnbFromVoice = (voiceValue: string) => {
    if (!voiceValue || isNaN(Number(voiceValue))) return ''
    const bnbRequired = Number(voiceValue) * PRESALE_PRICE
    return bnbRequired.toFixed(8)
  }
  
  // Handle BNB amount change
  const handleBnbChange = (value: string) => {
    setBnbAmount(value)
    setVoiceAmount(calculateVoiceFromBnb(value))
  }
  
  // Handle VOICE amount change
  const handleVoiceChange = (value: string) => {
    setVoiceAmount(value)
    setBnbAmount(calculateBnbFromVoice(value))
  }
  
  // Copy presale address
  const copyPresaleAddress = async () => {
    try {
      await navigator.clipboard.writeText(PRESALE_WALLET_ADDRESS)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }
  
  // Handle purchase
  const handlePurchase = async () => {
    if (!bnbAmount || !isConnected) return
    
    try {
      // Send BNB directly to VOICE smart contract for presale
      const result = await ethereumService.sendETH({
        recipient: '0x3c027958a3D4C4eDb008ee319692F7D387e6C5fB', // VOICE contract address
        amount: parseFloat(bnbAmount)
      })
      
      if (result.success) {
        // Create transaction record
        addTransaction({
          type: 'send',
          blockchain: 'ethereum',
          amount: bnbAmount,
          token: 'BNB',
          recipient: '0x3c027958a3D4C4eDb008ee319692F7D387e6C5fB',
          status: 'completed',
          hash: result.hash
        })
        
        setShowSuccess(true)
        setBnbAmount('')
        setVoiceAmount('')
      } else {
        console.error('Purchase failed:', result.error)
        alert(`Purchase failed: ${result.error}`)
      }
    } catch (error) {
      console.error('Purchase failed:', error)
      alert('Purchase failed. Please try again.')
    }
  }
  

  
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-yellow-900 to-orange-900 text-white p-4">
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Header with VOICE branding */}
        <motion.div
          className="text-center py-8"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex items-center justify-center space-x-4 mb-4">
            <div className="w-16 h-16 rounded-full bg-yellow-500 flex items-center justify-center">
              <img src="/Voice-32x32.svg" alt="VOICE" className="w-10 h-10" />
            </div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">
              VOICE Token Presale
            </h1>
          </div>
          <p className="text-white/70 text-lg">Secure your VOICE tokens at exclusive presale price</p>
        </motion.div>

        <div className="space-y-6">
          {/* Presale Information */}
          <motion.div
            className="glass rounded-2xl border border-white/20 p-6 space-y-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <div className="flex items-center space-x-2 text-white">
              <Info className="w-5 h-5" />
              <span className="font-semibold">Presale Information</span>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-1">
                <p className="text-white/70">Price per Token</p>
                <p className="text-white font-semibold">{PRESALE_PRICE} BNB</p>
              </div>
              <div className="space-y-1">
                <p className="text-white/70">Total Supply</p>
                <p className="text-white font-semibold">{TOTAL_SUPPLY.toLocaleString()} VOICE</p>
              </div>
              <div className="space-y-1">
                <p className="text-white/70">Min Purchase</p>
                <p className="text-white font-semibold">0.001 BNB</p>
              </div>
            </div>
            
            {/* Early Bird Bonuses */}
            <div className="space-y-3">
              <p className="text-white font-semibold flex items-center space-x-2">
                <span className="text-yellow-400">🎯</span>
                <span>Early Bird Bonuses</span>
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="bg-gradient-to-r from-yellow-500/20 to-orange-500/20 rounded-lg p-3 border border-yellow-500/30">
                  <p className="text-yellow-400 font-bold text-lg">+25%</p>
                  <p className="text-white/80 text-sm">First 100 Buyers</p>
                  <p className="text-white/60 text-xs">Limited Time Only</p>
                </div>
                <div className="bg-gradient-to-r from-orange-500/20 to-red-500/20 rounded-lg p-3 border border-orange-500/30">
                  <p className="text-orange-400 font-bold text-lg">+15%</p>
                  <p className="text-white/80 text-sm">Next 200 Buyers</p>
                  <p className="text-white/60 text-xs">Hurry Up!</p>
                </div>
                <div className="bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-lg p-3 border border-blue-500/30">
                  <p className="text-blue-400 font-bold text-lg">+10%</p>
                  <p className="text-white/80 text-sm">Next 300 Buyers</p>
                  <p className="text-white/60 text-xs">Don't Miss Out</p>
                </div>
              </div>
              <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 rounded-lg p-3 border border-green-500/20">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-green-400 font-semibold">🔥 Mega Bonus for Large Purchases</p>
                    <p className="text-white/70 text-sm">Purchase 1+ BNB and get an additional +5% bonus!</p>
                  </div>
                  <div className="text-green-400 font-bold text-xl">+5%</div>
                </div>
              </div>
            </div>
            
            {/* Countdown Timer */}
            <div className="space-y-2">
              <p className="text-white/70 text-sm">Presale Ends In:</p>
              <div className="grid grid-cols-4 gap-2">
                <div className="text-center p-2 bg-yellow-500/20 rounded-lg">
                  <p className="text-yellow-400 font-bold text-lg">{timeLeft.days}</p>
                  <p className="text-yellow-400/70 text-xs">Days</p>
                </div>
                <div className="text-center p-2 bg-yellow-500/20 rounded-lg">
                  <p className="text-yellow-400 font-bold text-lg">{timeLeft.hours}</p>
                  <p className="text-yellow-400/70 text-xs">Hours</p>
                </div>
                <div className="text-center p-2 bg-yellow-500/20 rounded-lg">
                  <p className="text-yellow-400 font-bold text-lg">{timeLeft.minutes}</p>
                  <p className="text-yellow-400/70 text-xs">Minutes</p>
                </div>
                <div className="text-center p-2 bg-yellow-500/20 rounded-lg">
                  <p className="text-yellow-400 font-bold text-lg">{timeLeft.seconds}</p>
                  <p className="text-yellow-400/70 text-xs">Seconds</p>
                </div>
              </div>
            </div>
          </motion.div>
          
          {/* Swap Interface */}
          <motion.div
            className="glass rounded-2xl border border-white/20 p-6 space-y-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            {/* From Token (BNB) */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-white/70 text-sm">You Pay</span>
                {isConnected && (
                  <span className="text-white/70 text-sm">
                    Balance: {parseFloat(balance).toFixed(4)} BNB
                  </span>
                )}
              </div>
              
              <div className="glass rounded-xl border border-white/20 p-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-full bg-yellow-500 flex items-center justify-center">
                    <img src="https://assets.coingecko.com/coins/images/825/small/bnb-icon2_2x.png" alt="BNB" className="w-6 h-6" />
                  </div>
                  <div className="flex-1">
                    <input
                      type="number"
                      value={bnbAmount}
                      onChange={(e) => handleBnbChange(e.target.value)}
                      placeholder="0.0"
                      className="w-full bg-transparent text-white text-xl font-semibold placeholder-white/50 focus:outline-none"
                    />
                    <p className="text-white/50 text-sm mt-1">BNB</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Swap Arrow */}
            <div className="flex justify-center">
              <div className="p-2 rounded-full bg-white/10 border border-white/20">
                <ArrowDownUp className="w-5 h-5 text-white/70" />
              </div>
            </div>
            
            {/* To Token (VOICE) */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-white/70 text-sm">You Receive</span>
              </div>
              
              <div className="glass rounded-xl border border-white/20 p-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-full bg-yellow-500 flex items-center justify-center">
                    <img src="/Voice-32x32.svg" alt="VOICE" className="w-6 h-6" />
                  </div>
                  <div className="flex-1">
                    <input
                      type="number"
                      value={voiceAmount}
                      onChange={(e) => handleVoiceChange(e.target.value)}
                      placeholder="0.0"
                      className="w-full bg-transparent text-white text-xl font-semibold placeholder-white/50 focus:outline-none"
                    />
                    <p className="text-white/50 text-sm mt-1">VOICE</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Purchase Button */}
            <button
              onClick={handlePurchase}
              disabled={!isConnected || !bnbAmount || parseFloat(bnbAmount) < 0.001}
              className="w-full bg-gradient-to-r from-yellow-500 to-orange-600 hover:from-yellow-600 hover:to-orange-700 disabled:from-gray-600 disabled:to-gray-700 disabled:cursor-not-allowed text-white font-semibold py-4 px-6 rounded-xl transition-all duration-200 flex items-center justify-center space-x-2"
            >
              <Send className="w-5 h-5" />
              <span>
                {!isConnected ? 'Connect Wallet' : 'Purchase VOICE Tokens'}
              </span>
            </button>
          </motion.div>
          
          {/* Presale Wallet Address */}
          <motion.div
            className="glass rounded-2xl border border-white/20 p-6 space-y-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <div className="flex items-center space-x-2 text-white">
              <Wallet className="w-5 h-5" />
              <span className="font-semibold">VOICE Smart Contract</span>
            </div>
            
            <div className="glass rounded-xl border border-white/20 p-4">
              <div className="flex items-center justify-between space-x-3">
                <div className="flex-1 min-w-0">
                  <p className="text-white font-mono text-sm break-all">
                    {PRESALE_WALLET_ADDRESS}
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={copyPresaleAddress}
                    className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
                    title="Copy Address"
                  >
                    {copied ? (
                      <CheckCircle className="w-4 h-4 text-green-400" />
                    ) : (
                      <Copy className="w-4 h-4 text-white/70" />
                    )}
                  </button>
                  <a
                    href={`https://bscscan.com/address/${PRESALE_WALLET_ADDRESS}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
                    title="View on BSCScan"
                  >
                    <ExternalLink className="w-4 h-4 text-white/70" />
                  </a>
                </div>
              </div>
            </div>
            
            <div className="text-sm text-white/70 space-y-1">
              <p>• Send BNB to this smart contract address to participate in the presale</p>
              <p>• VOICE tokens will be received instantly after transaction confirmation</p>
              <p>• Minimum purchase: 0.001 BNB</p>
            </div>
          </motion.div>
          
          {/* Success Modal */}
          <AnimatePresence>
            {showSuccess && (
              <motion.div
                className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <motion.div
                  className="glass rounded-2xl border border-white/20 p-6 max-w-sm w-full space-y-4"
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.9, opacity: 0 }}
                >
                  <div className="text-center space-y-3">
                    <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto">
                      <CheckCircle className="w-8 h-8 text-green-400" />
                    </div>
                    <h3 className="text-xl font-bold text-white">Purchase Successful!</h3>
                    <p className="text-white/70 text-sm">
                      Your VOICE tokens will be distributed after the presale ends.
                    </p>
                  </div>
                  
                  <button
                    onClick={() => setShowSuccess(false)}
                    className="w-full bg-gradient-to-r from-yellow-500 to-orange-600 hover:from-yellow-600 hover:to-orange-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200"
                  >
                    Continue
                  </button>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  )
}

export default PresalePage